import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { API_URL } from "../config/api";
import LoadingIndicator from "../Components/LoadingIndicator";

const PaymentView = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState(null);

  const formatDateTime = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date
      .toLocaleString("en-IN", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      })
      .replace(",", " ");
  };

  const formatAmount = (value) => {
    if (!value) return "-";
    return parseFloat(value).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  useEffect(() => {
    const fetchPayment = async () => {
      setIsLoading(true);
      try {
        const res = await axios.post(API_URL, {
          action: "getPaymentDetails",
          id: id,
        });

        if (res.data.success) {
          setData(res.data.data);
        } else {
          toast.error(res.data.message);
        }
      } catch (err) {
        toast.error("Failed to load payment");
      } finally {
        setIsLoading(false);
      }
    };

    fetchPayment();
  }, [id]);

  return (
    <div className="employee-form">
      <div className="content-header">
        <div className="row align-items-center">
          <div className="col-sm-6">
            <h1>Payment Details</h1>
            <p>View payment details.</p>
          </div>
          <div className="col-sm-6 text-right subscription-header-actions">
            <button className="btn btn-secondary" onClick={() => navigate(-1)}>
              Back
            </button>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="employee-loading">
          <LoadingIndicator size={120} />
        </div>
      ) : !data ? (
        <div className="card employee-content-card">
          <div className="card-body">
            <div className="employee-empty">No payment data found.</div>
          </div>
        </div>
      ) : (
        <>
        
          <div className="detail-section">
            <div className="detail-section-header">
              <i className="fas fa-info-circle"></i>
              <h3>Payment Information</h3>
            </div>

            <div className="detail-grid">
              <div className="detail-item">
                <div className="detail-icon">
                  <i className="fas fa-box"></i>
                </div>
                <div className="detail-content">
                  <label>Plan Name</label>
                  <span className="detail-value">{data.planName || "-"}</span>
                </div>
              </div>

              <div className="detail-item">
                <div className="detail-icon">
                  <i className="fas fa-clock"></i>
                </div>
                <div className="detail-content">
                  <label>Duration</label>
                  <span className="detail-value">
                    {data.duration || "-"} Months
                  </span>
                </div>
              </div>

              <div className="detail-item">
                <div className="detail-icon">
                  <i className="fas fa-rupee-sign"></i>
                </div>
                <div className="detail-content">
                  <label>Amount</label>
                  <span className="detail-value">
                    ₹ {formatAmount(data.amount)}
                  </span>
                </div>
              </div>

              <div className="detail-item">
                <div className="detail-icon">
                  <i className="fas fa-coins"></i>
                </div>
                <div className="detail-content">
                  <label>Currency</label>
                  <span className="detail-value">{data.currency || "-"}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="detail-section">
            <div className="detail-section-header">
              <i className="fas fa-receipt"></i>
              <h3>Transaction Details</h3>
            </div>

            <div className="detail-grid">
              <div className="detail-item">
                <div className="detail-icon">
                  <i className="fas fa-hashtag"></i>
                </div>
                <div className="detail-content">
                  <label>Order ID</label>
                  <span className="detail-value">{data.orderId || "-"}</span>
                </div>
              </div>

              <div className="detail-item">
                <div className="detail-icon">
                  <i className="fas fa-hashtag"></i>
                </div>
                <div className="detail-content">
                  <label>Payment ID</label>
                  <span className="detail-value">{data.paymentId || "-"}</span>
                </div>
              </div>

              <div className="detail-item">
                <div className="detail-icon">
                  <i className="fas fa-mobile"></i>
                </div>
                <div className="detail-content">
                  <label>VPA (UPI ID)</label>
                  <span className="detail-value">{data.vpa || "-"}</span>
                </div>
              </div>

              <div className="detail-item">
                <div className="detail-icon">
                  <i className="fas fa-exchange-alt"></i>
                </div>
                <div className="detail-content">
                  <label>UPI Transaction ID</label>
                  <span className="detail-value">
                    {data.upiTransactionId || "-"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="detail-section">
            <div className="detail-section-header status">
              <i className="fas fa-toggle-on"></i>
              <h3>Status</h3>
            </div>

            <div className="detail-grid">
              <div className="detail-item">
                <div className="detail-icon status">
                  <i className="fas fa-check-circle"></i>
                </div>
                <div className="detail-content">
                  <label>Status</label>
                  <span
                    className={`status-badge ${data.status?.toLowerCase()}`}
                  >
                    {data.status || "-"}
                  </span>
                </div>
              </div>

              <div className="detail-item">
                <div className="detail-icon">
                  <i className="fas fa-credit-card"></i>
                </div>
                <div className="detail-content">
                  <label>Method</label>
                  <span className="detail-value">
                    {typeof data.method === "object"
                      ? data.method.source
                      : data.method || "-"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="detail-section">
            <div className="detail-section-header financial">
              <i className="fas fa-rupee-sign"></i>
              <h3>Charges</h3>
            </div>

            <div className="detail-grid">
              <div className="detail-item">
                <div className="detail-icon financial">
                  <i className="fas fa-money-bill-wave"></i>
                </div>
                <div className="detail-content">
                  <label>Razorpay Fee</label>
                  <span className="detail-value">
                    ₹ {formatAmount(data.razorpayFee)}
                  </span>
                </div>
              </div>

              <div className="detail-item">
                <div className="detail-icon financial">
                  <i className="fas fa-receipt"></i>
                </div>
                <div className="detail-content">
                  <label>Razorpay Tax</label>
                  <span className="detail-value">
                    ₹ {formatAmount(data.razorpayTax)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="detail-section">
            <div className="detail-section-header">
              <i className="fas fa-user"></i>
              <h3>Payer Info</h3>
            </div>

            <div className="detail-grid">
              <div className="detail-item">
                <div className="detail-icon">
                  <i className="fas fa-envelope"></i>
                </div>
                <div className="detail-content">
                  <label>Email</label>
                  <span className="detail-value">{data.payerEmail || "-"}</span>
                </div>
              </div>

              <div className="detail-item">
                <div className="detail-icon">
                  <i className="fas fa-phone"></i>
                </div>
                <div className="detail-content">
                  <label>Contact</label>
                  <span className="detail-value">
                    {data.payerContact || "-"}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="detail-section">
            <div className="detail-section-header">
              <i className="fas fa-sticky-note"></i>
              <h3>Notes</h3>
            </div>

            <div className="detail-grid">
              <div className="detail-item">
                <div className="detail-icon">
                  <i className="fas fa-align-left"></i>
                </div>
                <div className="detail-content">
                  <label>Notes</label>
                  <span className="detail-value">
                    {data.notes
                      ? typeof data.notes === "object"
                        ? Object.values(data.notes).join(", ")
                        : data.notes
                      : "-"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="detail-section">
            <div className="detail-section-header status">
              <i className="fas fa-exclamation-triangle"></i>
              <h3>Error Details</h3>
            </div>

            <div className="detail-grid">
              <div className="detail-item">
                <div className="detail-icon status">
                  <i className="fas fa-code"></i>
                </div>
                <div className="detail-content">
                  <label>Razor pay Error Code</label>
                  <span className="detail-value">
                    {data.razorpayErrorCode || "-"}
                  </span>
                </div>
              </div>

              <div className="detail-item">
                <div className="detail-icon status">
                  <i className="fas fa-info-circle"></i>
                </div>
                <div className="detail-content">
                  <label>Razor pay Error Description</label>
                  <span className="detail-value">
                    {data.razorpayErrorDescription || "-"}
                  </span>
                </div>
              </div>

              <div className="detail-item">
                <div className="detail-icon status">
                  <i className="fas fa-database"></i>
                </div>
                <div className="detail-content">
                  <label>Razor pay Error Source</label>
                  <span className="detail-value">
                    {data.razorpayErrorSource || "-"}
                  </span>
                </div>
              </div>

              <div className="detail-item">
                <div className="detail-icon status">
                  <i className="fas fa-stream"></i>
                </div>
                <div className="detail-content">
                  <label>Razor pay Error Step</label>
                  <span className="detail-value">
                    {data.razorpayErrorStep || "-"}
                  </span>
                </div>
              </div>

              <div className="detail-item">
                <div className="detail-icon status">
                  <i className="fas fa-question-circle"></i>
                </div>
                <div className="detail-content">
                  <label>Razor pay Error Reason</label>
                  <span className="detail-value">
                    {data.razorpayErrorReason || "-"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="detail-section audit">
            <div className="detail-section-header audit">
              <i className="fas fa-history"></i>
              <h3>Audit Trail</h3>
            </div>

            <div className="detail-grid">
              <div className="detail-item">
                <div className="detail-icon audit">
                  <i className="fas fa-clock"></i>
                </div>
                <div className="detail-content">
                  <label>Created At</label>
                  <span className="detail-value">
                    {formatDateTime(data.createdAt)}
                  </span>
                </div>
              </div>

              <div className="detail-item">
                <div className="detail-icon audit">
                  <i className="fas fa-clock"></i>
                </div>
                <div className="detail-content">
                  <label>Updated At</label>
                  <span className="detail-value">
                    {formatDateTime(data.updatedAt)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PaymentView;
